import { toast } from 'react-toastify';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { ApiService } from '../../services/index';
import { downloadTextFile } from '../../utils/index';
import { useAppMutation } from '../use-app-mutation.hook';
import { useAppQuery } from '../use-app-query.hook';
import { useInvalidateQueries } from '../use-invalidate-queries.hook';

// Mirrors the backend enums/entity (admin-panel: cap-table-integrity.util.ts + cap-table-integrity-scan.entity.ts).
export enum CapTableIntegrityColumn {
  DILUTED = 'diluted',
  ISSUED = 'issued',
  SHARE_CLASS_DILUTED = 'share-class-diluted',
  SHARE_CLASS_ISSUED = 'share-class-issued',
  SHARE_CLASS_VOTING = 'share-class-voting',
  VOTING = 'voting',
}

export enum CapTableIntegrityScanStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  PENDING = 'pending',
  PROCESSING = 'processing',
}

export type CapTableColumnIssue = {
  actual: number;
  column: CapTableIntegrityColumn;
  delta: number;
  expected: number;
};

export type CapTableIntegrityCompanyError = {
  companyId: string;
  companyName: string;
  computationError?: string;
  issues: CapTableColumnIssue[];
};

export type CapTableIntegrityScan = {
  errorCount: number;
  errors: CapTableIntegrityCompanyError[];
  id: string;
  lastError: null | string;
  processedCount: number;
  status: CapTableIntegrityScanStatus;
  tolerance: number;
  totalCompanies: null | number;
};

const SCAN_ROUTE_PATH = 'cap-table-integrity-scans';
const POLL_INTERVAL_MS = 4000;

const COLUMN_LABEL: Record<CapTableIntegrityColumn, string> = {
  [CapTableIntegrityColumn.ISSUED]: 'Issued %',
  [CapTableIntegrityColumn.DILUTED]: 'Diluted %',
  [CapTableIntegrityColumn.VOTING]: 'Voting %',
  [CapTableIntegrityColumn.SHARE_CLASS_ISSUED]: 'By share class — Issued %',
  [CapTableIntegrityColumn.SHARE_CLASS_DILUTED]: 'By share class — Diluted %',
  [CapTableIntegrityColumn.SHARE_CLASS_VOTING]: 'By share class — Voting %',
};

export const isCapTableScanActive = (status?: CapTableIntegrityScanStatus) =>
  status === CapTableIntegrityScanStatus.PENDING ||
  status === CapTableIntegrityScanStatus.PROCESSING;

const formatPercent = (fraction: number) => `${(fraction * 100).toFixed(2)}%`;

export const buildCapTableScanReport = (scan: CapTableIntegrityScan): string => {
  const lines = [
    'CapQuest — Cap table integrity scan',
    `Generated: ${new Date().toISOString()}`,
    `Companies scanned: ${scan.processedCount}${scan.totalCompanies ? ` / ${scan.totalCompanies}` : ''}`,
    `Companies with errors: ${scan.errorCount}`,
    `Tolerance: ${formatPercent(scan.tolerance)}`,
    '',
  ];

  // The backend caps the stored per-company detail; keep the report honest about it.
  if (scan.errorCount > scan.errors.length) {
    lines.push(
      `Showing the first ${scan.errors.length} of ${scan.errorCount} companies with errors.`,
      '',
    );
  }

  scan.errors.forEach((company, index) => {
    lines.push(
      `${index + 1}. ${company.companyName || '(unnamed company)'} (${company.companyId})`,
    );

    if (company.computationError) {
      lines.push(`   - Could not compute cap table: ${company.computationError}`);
    }

    company.issues.forEach((issue) => {
      const label = COLUMN_LABEL[issue.column] ?? issue.column;
      lines.push(
        `   - ${label}: totals ${formatPercent(issue.actual)} (expected 100.00%, off by ${formatPercent(issue.delta)})`,
      );
    });

    lines.push('');
  });

  return lines.join('\n');
};

export const downloadCapTableScanReport = (scan: CapTableIntegrityScan) => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  downloadTextFile(`cap-table-integrity-${timestamp}.txt`, buildCapTableScanReport(scan));
};

// Read-only poller for the latest scan run. Side-effect free, so it can be mounted by both the
// header button and the persistent banner — react-query dedupes to a single background request.
export const useCapTableScanStatus = () => {
  const { data: scan } = useAppQuery<CapTableIntegrityScan | null>({
    queryKey: [QueryKey.SCAN_CAP_TABLE, 'latest'],
    queryFn: () =>
      ApiService.get<CapTableIntegrityScan | null>({
        endpoint: BackendRoute.COMPANIES,
        routePath: [SCAN_ROUTE_PATH, 'latest'],
      }),
    refetchInterval: (query) =>
      isCapTableScanActive(query.state.data?.status ?? undefined) ? POLL_INTERVAL_MS : false,
    defaultErrorHandling: false,
  });

  return { isActive: isCapTableScanActive(scan?.status), scan: scan ?? null };
};

// Starts (or re-attaches to) a background scan. The banner picks up progress via the poller above.
export const useStartCapTableScan = () => {
  const { invalidateQuery } = useInvalidateQueries(QueryKey.SCAN_CAP_TABLE);

  const { isPending, mutate: startScan } = useAppMutation<CapTableIntegrityScan>(
    [QueryKey.SCAN_CAP_TABLE],
    {
      defaultErrorHandling: false,
      mutationFn: () =>
        ApiService.post<CapTableIntegrityScan>(
          { endpoint: BackendRoute.COMPANIES, routePath: [SCAN_ROUTE_PATH] },
          { body: {} },
        ),
      onSuccess: () => {
        // Refresh the poller immediately so the banner reflects the newly-started run without waiting.
        invalidateQuery();
        toast.info('Cap table scan started — running in the background. You can keep working.');
      },
      onError: () => {
        toast.error('Could not start cap table scan. Please try again.');
      },
    },
  );

  return { isStarting: isPending, startScan };
};
